/**
 * SignalrRealtimeService - Browser connection to Azure SignalR for in-app alerts
 *
 * Until this service existed the dashboard had no SignalR client at all, so
 * signalr-scanninja-alerts-prd never saw a connection and every
 * push_signalr_message call had no one to deliver to.
 *
 * Flow:
 * 1. POST {alertsUrl}/negotiate with the user's Firebase ID token.
 *    (func-scanninja-alerts verifies it and returns the Azure SignalR client
 *    URL plus a JWT whose `nameid` is the Firebase uid.)
 * 2. @microsoft/signalr follows that redirect and connects to the hub.
 * 3. Messages sent server-side to users/{uid} arrive on the client methods
 *    below and are pushed into AlertNotificationBridgeService, which feeds
 *    the header bell.
 *
 * Browser-only; no-op during SSR. Never uses the SignalR access key or the
 * alerts function key.
 */
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Auth } from '@angular/fire/auth';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AlertNotificationBridgeService } from './alert-notification-bridge.service';
import { LoggingService } from './logging.service';

/** Client method names the alerts backend targets (see alert-notification.service getSignalRTarget). */
const CLIENT_METHODS: ReadonlyArray<{ method: string; type: string }> = [
  { method: 'vulnerabilityAlert', type: 'vulnerability_scan' },
  { method: 'scanComplete', type: 'scan_completion' },
  { method: 'ticketUpdate', type: 'ticket_update' },
  { method: 'complianceAlert', type: 'compliance_drift' },
  { method: 'incidentAlert', type: 'incident_response' },
  { method: 'systemAlert', type: 'system_status' },
  { method: 'generalAlert', type: 'general' },
  { method: 'darkWebAlert', type: 'dark_web' },
  { method: 'alert', type: 'general' },
];

interface RealtimeAlertPayload {
  id?: string;
  type?: string;
  severity?: string;
  title?: string;
  message?: string;
  metadata?: Record<string, unknown>;
  actions?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class SignalrRealtimeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly auth = inject(Auth);
  private readonly zone = inject(NgZone);
  private readonly alertBridge = inject(AlertNotificationBridgeService);
  private readonly logging = inject(LoggingService);

  private connection: HubConnection | null = null;
  private startPromise: Promise<void> | null = null;

  /** Connection state, for diagnostics pages. */
  get state(): HubConnectionState {
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }

  /**
   * Connect for the signed-in user. Safe to call repeatedly; failures are
   * logged and never thrown, so a SignalR outage cannot break page load.
   */
  start(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    if (this.startPromise) {
      return this.startPromise;
    }
    const baseUrl = (environment.azureFunctions?.alertsUrl || '').replace(/\/+$/, '');
    if (!baseUrl || !this.auth.currentUser) {
      return Promise.resolve();
    }

    const connection = new HubConnectionBuilder()
      .withUrl(baseUrl, {
        // Firebase refreshes the ID token as needed; negotiate verifies it.
        accessTokenFactory: async () =>
          (await this.auth.currentUser?.getIdToken()) ?? '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
      .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information)
      .build();

    for (const { method, type } of CLIENT_METHODS) {
      connection.on(method, (payload: unknown) =>
        this.zone.run(() => this.handleMessage(type, payload)),
      );
    }
    connection.onclose((error) => {
      if (error) {
        this.logging.logWarning('SignalR connection closed', { message: error.message });
      }
      this.startPromise = null;
    });

    this.connection = connection;
    // Keep SignalR's timers and websocket callbacks out of change detection.
    this.startPromise = this.zone.runOutsideAngular(() =>
      connection.start().catch((error: unknown) => {
        this.logging.logWarning('SignalR connection failed to start', {
          message: error instanceof Error ? error.message : String(error),
        });
        this.connection = null;
        this.startPromise = null;
      }),
    );
    return this.startPromise;
  }

  /** Disconnect (logout / component teardown). */
  async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    this.startPromise = null;
    if (connection) {
      try {
        await connection.stop();
      } catch {
        // already closed
      }
    }
  }

  private handleMessage(fallbackType: string, raw: unknown): void {
    const payload = this.normalize(raw);
    if (!payload) {
      return;
    }

    // The browser that originated an alert already added it locally
    // (AlertNotificationService.sendAlert); don't show it twice.
    if (
      payload.id &&
      this.alertBridge
        .getCurrentAlerts()
        .some((existing) => existing.metadata?.alertId === payload.id)
    ) {
      return;
    }

    this.alertBridge.addAlert({
      type: payload.type || fallbackType,
      severity: payload.severity || 'info',
      title: payload.title || 'Scan Ninja notification',
      message: payload.message || '',
      channels: ['signalr'],
      metadata: {
        ...(payload.metadata || {}),
        alertId: payload.id,
        actions: payload.actions,
        source: 'signalr',
      },
    });
  }

  /** Accept an object, a JSON string, or a plain string message. */
  private normalize(raw: unknown): RealtimeAlertPayload | null {
    if (raw && typeof raw === 'object') {
      return raw as RealtimeAlertPayload;
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          return parsed as RealtimeAlertPayload;
        }
      } catch {
        // plain text message
      }
      return { message: raw };
    }
    return null;
  }
}
